import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  alpha,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  Typography,
  Tooltip,
  Divider,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";

import { useAuth } from "../context/AuthContext";
import { logoutAndResetPortalSession } from "../lib/portalSession";

const SIDEBAR_WIDTH = 220;

const sidebarBackground = (theme: Theme) =>
  `linear-gradient(162deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.main} 68%, ${alpha(theme.palette.primary.dark, 0.86)} 138%)`;

const NAV_ITEMS = [
  { to: "/",             label: "Overview",       icon: <GridViewRoundedIcon fontSize="small" />,           exact: true },
  { to: "/conversations",label: "Conversations",  icon: <ChatBubbleOutlineRoundedIcon fontSize="small" /> },
  { to: "/bookings",     label: "Bookings",        icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { to: "/calendar",     label: "Calendar",        icon: <EventOutlinedIcon fontSize="small" /> },
  { to: "/customers",    label: "Customers",       icon: <PeopleOutlineRoundedIcon fontSize="small" /> },
  { to: "/analytics",   label: "Analytics",       icon: <BarChartRoundedIcon fontSize="small" /> },
  { to: "/integrations", label: "Integrations",   icon: <TuneRoundedIcon fontSize="small" /> },
  { to: "/billing",     label: "Billing",          icon: <CreditCardOutlinedIcon fontSize="small" /> },
  { to: "/settings",    label: "Settings",        icon: <SettingsOutlinedIcon fontSize="small" /> },
];

const Sidebar: React.FC = () => {
  const { currentUser } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logoutAndResetPortalSession(navigate);
    } catch (e) {
      console.error("Logout failed:", e);
    }
  };

  const isActive = (item: { to: string; exact?: boolean }) =>
    item.exact
      ? location.pathname === item.to
      : location.pathname.startsWith(item.to);

  const navContent = (onNav?: () => void) => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <Box sx={{ pl: 3, pt: 3, pb: 2.5 }}>
        <img
          src="/media/setin_logo.png"
          alt="Setin"
          style={{ width: "140px", height: "auto", display: "block" }}
        />
      </Box>

      {/* Nav items */}
      <List sx={{ px: 1.5, flex: 1 }}>
        {NAV_ITEMS.map((item) => {
          const active = isActive(item);
          return (
            <ListItemButton
              key={item.to}
              component={Link}
              to={item.to}
              onClick={onNav}
              sx={{
                borderRadius: "10px",
                mb: 0.5,
                py: 1,
                px: 1.5,
                color: active ? "#fff" : "rgba(255,255,255,0.6)",
                backgroundColor: active ? "#C9734F" : "transparent",
                "&:hover": {
                  backgroundColor: active ? "#b8613f" : "#1E293B",
                  color: "#fff",
                },
                transition: "background-color 0.15s ease, color 0.15s ease",
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 32,
                  color: "inherit",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: active ? 600 : 400,
                  letterSpacing: "0.01em",
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* User + logout */}
      <Box>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2 }} />
        <Box
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#1E293B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonOutlineRoundedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.6)" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.5)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {currentUser?.email ?? ""}
            </Typography>
          </Box>
          <Tooltip title="Log out" placement="top">
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: "#fff" } }}
            >
              <LogoutRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <Box
        sx={{
          position: "fixed",
          top: 12,
          left: 12,
          zIndex: 1300,
          display: { xs: "block", md: "none" },
        }}
      >
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{ color: "#fff", backgroundColor: "#0F172A" }}
          size="large"
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            background: sidebarBackground,
            color: "#fff",
            width: SIDEBAR_WIDTH,
            border: "none",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: "rgba(255,255,255,0.5)" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {navContent(() => setMobileOpen(false))}
      </Drawer>

      {/* Desktop Sidebar */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          height: "100vh",
          background: sidebarBackground,
          color: "#fff",
          position: "fixed",
          top: 0,
          left: 0,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {navContent()}
      </Box>
    </>
  );
};

export default Sidebar;

export { SIDEBAR_WIDTH };
